import {
  Button,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  DialogActions,
  Alert,
  Box,
  Divider
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import AlertDialog from "elements/AlertDialog";
import { ApiRequest } from "GlobalFunctions";
import LoadingCircle from "elements/LoadingCircle";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from "react-router-dom";

export default function PurchaseOrderPreviewer(props: any) {
  const { open, setOpen, data, tempXmlPath } = props;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);

    // Crear FormData con el temp_xml_path para que el backend use el XML guardado
    const formData = new FormData();
    formData.append("formValues", JSON.stringify({
      temp_xml_path: tempXmlPath // Enviar el path del XML temporal
    }));

    await ApiRequest({
      url: "/purchaseorders/uploadXML",
      method: "post",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      body: formData,
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar(
            `Factura #${response.invoice_number} importada correctamente con ${response.line_items_count} items`,
            { variant: "success" }
          );
          setOpen(false);

          // Navegar a la lista de órdenes de compra
          navigate("/purchaseorders");
        } else {
          enqueueSnackbar(response.message || "Error al importar XML", { variant: "error" });
        }
        setLoading(false);
      },
    });
  };

  if (!data || !data.invoice_info) {
    return null;
  }

  const {
    invoice_info,
    supplier,
    totals,
    items,
    statistics,
    warnings,
    errors,
    is_duplicate
  } = data;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="dialog-title"
        fullWidth={true}
        maxWidth="lg"
      >
        <LoadingCircle loading={loading} className="innerLoader" />
        <div className="DialogContainer">
          <div className="ModuleWrapper">
            <div className="boxContainerHeader">
              <div className="boxContainerTitle">
                <h2 className="h2_title">Previsualizar Factura XML DIAN</h2>
                <span className="h2_subtitle">
                  Factura: {invoice_info.invoice_number}
                </span>
              </div>
            </div>

            <div className="content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Alerts */}
              {is_duplicate && (
                <Alert severity="error" style={{ marginBottom: '16px' }}>
                  Esta factura ya existe en el sistema
                </Alert>
              )}

              {errors && errors.length > 0 && (
                <Alert severity="error" style={{ marginBottom: '16px' }}>
                  {errors.map((error: string, index: number) => (
                    <div key={index}>{error}</div>
                  ))}
                </Alert>
              )}

              {warnings && warnings.length > 0 && (
                <Alert severity="warning" style={{ marginBottom: '16px' }}>
                  {warnings.map((warning: string, index: number) => (
                    <div key={index}>{warning}</div>
                  ))}
                </Alert>
              )}

              {/* Invoice Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Información de la Factura
                </Typography>
                <Paper sx={{ p: 2 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        Número de Factura
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {invoice_info.invoice_number}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Emisión
                      </Typography>
                      <Typography variant="body1">
                        {invoice_info.issue_date}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        Orden de Compra
                      </Typography>
                      <Typography variant="body1">
                        {invoice_info.order_reference || 'N/A'}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        CUFE/UUID
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {invoice_info.uuid}
                      </Typography>
                    </div>
                  </div>
                </Paper>
              </Box>

              {/* Supplier Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Información del Proveedor
                </Typography>
                {!supplier.found_in_db && (
                  <Alert severity="error" style={{ marginBottom: '16px' }}>
                    <strong>Proveedor no registrado</strong>
                    <br />
                    El proveedor con NIT {supplier.nit} ({supplier.name}) no existe en el sistema.
                    <br />
                    Debe crear el proveedor antes de poder importar esta factura.
                  </Alert>
                )}
                <Paper sx={{ p: 2 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        NIT
                      </Typography>
                      <Typography variant="body1">
                        {supplier.nit}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        Nombre
                      </Typography>
                      <Typography variant="body1">
                        {supplier.name}
                      </Typography>
                    </div>
                    <div>
                      <Chip
                        icon={supplier.found_in_db ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
                        label={supplier.found_in_db ? "Proveedor registrado" : "Proveedor NO registrado"}
                        color={supplier.found_in_db ? "success" : "warning"}
                        variant="outlined"
                      />
                    </div>
                  </div>
                </Paper>
              </Box>

              {/* Totals */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Totales
                </Typography>
                <Paper sx={{ p: 2 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        Subtotal
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${totals.subtotal?.toLocaleString() || 0}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        Impuestos
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${totals.total_tax?.toLocaleString() || 0}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        Total
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        ${totals.payable?.toLocaleString() || 0}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="text.secondary">
                        Líneas
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {invoice_info.line_count} items
                      </Typography>
                    </div>
                  </div>
                </Paper>
              </Box>

              {/* Statistics */}
              <Box sx={{ mb: 3 }}>
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                  <Chip
                    label={`${statistics.total_items} productos en total`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label={`${statistics.matched_products} productos encontrados`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`${statistics.unmatched_products} productos NO encontrados`}
                    color="warning"
                    variant="outlined"
                  />
                </div>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Items Table */}
              <Typography variant="h6" gutterBottom>
                Items de la Factura
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Línea</TableCell>
                      <TableCell>Código</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Cajas</TableCell>
                      <TableCell align="right">Unidades</TableCell>
                      <TableCell align="right">Precio Unit.</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items && items.map((item: any, index: number) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: item.matched ? 'inherit' : '#fff3cd'
                        }}
                      >
                        <TableCell>{item.line_id}</TableCell>
                        <TableCell>{item.barcode || item.product_code || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.description}
                          </Typography>
                          {item.matched && item.product_name_db && (
                            <Typography variant="caption" color="text.secondary">
                              BD: {item.product_name_db}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {item.boxes || 0}
                          {item.units_per_box > 1 && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              ({item.units_per_box}/caja)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{item.units || 0}</TableCell>
                        <TableCell align="right">
                          ${item.price?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell align="right">
                          ${item.total?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={item.matched ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
                            label={item.matched ? "Encontrado" : "No encontrado"}
                            color={item.matched ? "success" : "warning"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        </div>

        <DialogActions style={{ padding: '16px', backgroundColor: 'white', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setOpen(false)} variant="outlined">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenAlert(true)}
            disabled={is_duplicate || (errors && errors.length > 0) || !supplier.found_in_db}
            title={!supplier.found_in_db ? "El proveedor debe estar registrado en el sistema antes de importar" : ""}
          >
            Importar Factura
          </Button>
        </DialogActions>

        <AlertDialog
          open={openAlert}
          setOpen={setOpenAlert}
          send={handleSubmit}
          subtitle="¿Estás seguro de importar esta factura XML?"
        />
      </Dialog>
    </>
  );
}
